import { render } from "preact";
import { useRef, useState } from "preact/hooks";
import { modal } from "../../ui/modal";
import { api } from "../../api";
import { toastError } from "../../ui/messages";
import { savedlsettings } from "../download";
import { addDownloadCart } from "../downloadActions";
import { download } from "../downloadActions";
import { clearDownloadCart } from "../cart";
import type { DownloadCartResponse } from "../cart";
import { ID_DOWNLOAD_CART_TARGET } from "../../utils/elementIds";
import { hide, fadeOut } from "../../utils/dom";
import { BrowserDownloadGate } from "../browserDownload/BrowserDownloadPanel";

function LimitView() {
  return (
    <>
      <div style="font-size: 1.5em; text-align: center;">Licensing limit hit</div>
      <div style="font-size: 1em; text-align: center; margin: 10px 0px 0px 0px; line-height: 1.6em;">
        You need to contact{" "}
        <a
          style="border-bottom: 1px solid #000; font-weight: 600;"
          href="mailto:licensing@pictufy.com"
        >
          licensing@pictufy.com
        </a>{" "}
        to increase capacity.
      </div>
    </>
  );
}

function DownloadingView({ perc }: { perc: number }) {
  const progressText =
    perc > 0 ? (
      `${perc}%`
    ) : (
      <span style="font-size: 2em; position:relative; bottom: 15px; left: 2px;">
        <i class="far fa-clock" />
      </span>
    );
  const progressTitle =
    perc > 0 ? "Fetching files. Please wait..." : "Waiting for an available download slot...";

  return (
    <div class="download-cart-download-in-progress" id="download-cart-download-in-progress">
      <div class="download-cart-progress">{progressText}</div>
      <div class="download-cart-title">{progressTitle}</div>
    </div>
  );
}

interface CartItem {
  id: number;
  imgFile: string;
  title: string;
  width: number;
  height: number;
}

interface CartSettings {
  interior: boolean;
  dlRes: string;
  dlResCustom: string;
}

/**
 * The cart's Download button, with an immediate busy state.
 *
 * `download()` makes a `getCart` round trip before anything visible happens — it asks the
 * server which downloader this member gets — so the button sat inert long enough for people
 * to click it again. Feedback is applied on click rather than after the response, which is
 * the only point at which it helps.
 *
 * The busy state is not cleared on success: every path ends by remounting the cart (into the
 * progress view, or the browser downloader's overlay), which discards this component's state
 * anyway. `finally` exists for the failure path, where the cart stays put and the button has
 * to become usable again.
 */
function DownloadButton({ ids }: { ids: string }) {
  const [preparing, setPreparing] = useState(false);
  // The guard has to be a ref, not the state above. `setPreparing` is asynchronous, so three
  // synchronous clicks all read `preparing === false` and start three downloads — which a
  // test caught. The ref flips on the first click, before the re-render.
  const busy = useRef(false);

  return (
    <span
      style="display: block; text-align: center; font-weight: 900; font-size: 1em; border: 2px solid #17bebb;"
      class={preparing ? "button medium green cart-busy" : "button medium green"}
      onClick={() => {
        if (busy.current) return;
        busy.current = true;
        setPreparing(true);
        void download(ids).finally(() => {
          busy.current = false;
          setPreparing(false);
        });
      }}
    >
      {preparing ? (
        <>
          <i class="fas fa-spinner fa-spin" /> Preparing...
        </>
      ) : (
        <>
          <i class="fa fa-download" /> Download
        </>
      )}
    </span>
  );
}

function CartView({
  settings,
  items,
  ids,
}: {
  settings?: CartSettings | undefined;
  items: CartItem[];
  ids: string;
}) {
  return (
    <>
      <div style="border-bottom: 1px dashed #000; padding: 0px 0px 15px 0px;">
        <table cellPadding="0" cellSpacing="0" width="100%">
          <tr>
            <td>
              <span
                style="display: block;"
                class="button medium black"
                onClick={() => {
                  modal("defaultinterior", "large");
                }}
              >
                <i class="fas fa-couch" /> Select Interior Images
              </span>
            </td>
            <td width="10">&nbsp;</td>
            <td>
              <span
                style="display: block;"
                class="button medium yellow"
                onClick={() => clearDownloadCart()}
              >
                <i class="fa fa-trash" /> Clear all
              </span>
            </td>
          </tr>
        </table>
      </div>
      <div style="padding: 15px 0px 15px 0px; text-align: left;">
        <div style="margin: 0px 0px 15px 0px;">
          <table cellPadding="0" cellSpacing="0">
            <tr>
              <td>
                <span class="dl_option_label">INTERIORS</span>
              </td>
              <td class="dl_option_td">
                <input
                  onChange={() => savedlsettings()}
                  type="checkbox"
                  id="dl_interiors"
                  checked={settings?.interior ?? false}
                />
                <label for="dl_interiors">Include in download</label>
              </td>
            </tr>
            <tr>
              <td>
                <span class="dl_option_label">RESOLUTION</span>
              </td>
              <td class="dl_option_td">
                <input
                  onChange={() => savedlsettings()}
                  type="radio"
                  name="dl_res"
                  value="1"
                  id="dl_res_1"
                  checked={settings?.dlRes === "1"}
                />
                <label for="dl_res_1">Max</label>
                <input
                  onChange={() => savedlsettings()}
                  type="radio"
                  name="dl_res"
                  value="2"
                  id="dl_res_2"
                  checked={settings?.dlRes === "2"}
                />
                <label for="dl_res_2">
                  <span id="dl_res_custom">{settings?.dlResCustom ?? ""}</span>px
                </label>
                <span
                  class="button small black"
                  onClick={() => {
                    modal("setcustomdl", "small");
                  }}
                >
                  Customise
                </span>
              </td>
            </tr>
          </table>
        </div>
        <DownloadButton ids={ids} />
      </div>
      {items.map((item) => (
        <div class="download-cart-row" key={item.id}>
          <table cellPadding="0" cellSpacing="0" width="100%">
            <tr>
              <td width="100">
                <div class="download-cart-row-img">
                  <a href={`/view/${item.id}`}>
                    <img src={`https://1x.com/images/user/${item.imgFile}-ld.jpg`} />
                  </a>
                </div>
              </td>
              <td>
                <div class="download-cart-row-title">
                  <a style="color: #000;" href={`/view/${item.id}`}>
                    {item.title}
                  </a>
                </div>
                <div class="download-cart-row-id">#{item.id}</div>
                <div class="download-cart-row-resolution">
                  {item.width}x{item.height} pixels
                </div>
              </td>
              <td width="50" align="right">
                <span
                  onClick={() => addDownloadCart(String(item.id))}
                  class="button small red dlcartbutton"
                >
                  <i class="fa fa-trash" />
                </span>
              </td>
            </tr>
          </table>
        </div>
      ))}
    </>
  );
}

export function DownloadCart({ response }: { response: DownloadCartResponse }) {
  if (response.state === "limit") {
    return <LimitView />;
  }

  if (response.state === "downloading") {
    return <DownloadingView perc={response.perc ?? 0} />;
  }

  if (response.state === "cart") {
    const { settings, items = [], ids = "" } = response;
    if (items.length === 0) return null;
    // Browser downloader only. The gate renders nothing and disables nothing unless a
    // download is running or left unfinished, so the Lambda path's cart is unchanged.
    if (response.browserDownloader === true) {
      return (
        <BrowserDownloadGate>
          <CartView settings={settings} items={items} ids={ids} />
        </BrowserDownloadGate>
      );
    }
    return <CartView settings={settings} items={items} ids={ids} />;
  }

  return null;
}

/**
 * Render the download cart into #download-cart-target.
 * Call this wherever you previously did:
 *   setHtml("download-cart-target", renderDownloadCart(response));
 */
let cartInitialized = false;

export function mountDownloadCart(response: DownloadCartResponse): void {
  const container = document.getElementById(ID_DOWNLOAD_CART_TARGET);
  if (container) {
    if (!cartInitialized) {
      container.textContent = "";
      cartInitialized = true;
    }
    render(<DownloadCart response={response} />, container);
  }
}

/**
 * Properly unmount the Preact tree from #download-cart-target.
 * Use this instead of jQuery .html("") to avoid corrupting Preact state.
 */
export function unmountDownloadCart(): void {
  const container = document.getElementById(ID_DOWNLOAD_CART_TARGET);
  if (container) {
    render(null, container);
  }
}

// --- Download progress status (replaces updateDownloadStatus innerHTML) ---

function CompletedView({
  perc,
  dlId,
  dlUrl,
}: {
  perc: number;
  dlId: string;
  dlUrl?: string | undefined;
}) {
  return (
    <>
      <div class="download-cart-progress">{perc}%</div>
      <div class="download-cart-title">Your download is now ready</div>
      <div id="download-finished" class="download-cart-button">
        <span
          class="button medium green"
          onClick={async () => {
            // dlUrl is the S3 presigned archive URL; it is only absent when presigning
            // failed server-side — surface that instead of navigating to a dead link.
            if (!dlUrl) {
              toastError("Download link is not available yet — please try again in a moment");
              return;
            }
            try {
              await api.downloadCart.complete({ id: dlId, clearCart: true });
            } catch {
              // proceed with download even if marking fails
            }
            fadeOut("download-cart");
            fadeOut("download-cart-button");
            hide("download-cart-back");
            const countEl = document.getElementById("download-cart-count");
            if (countEl) countEl.innerHTML = "0";
            location.href = dlUrl;
          }}
        >
          <i class="fa fa-download" /> Download
        </span>
      </div>
    </>
  );
}

function InProgressView({ perc }: { perc: number }) {
  return (
    <>
      <div class="download-cart-progress">{perc}%</div>
      <div class="download-cart-title">Fetching files. Please wait...</div>
    </>
  );
}

function WaitingView() {
  return (
    <>
      <div class="download-cart-progress">
        <span style="font-size: 2em; position:relative; bottom: 15px; left: 2px;">
          <i class="far fa-clock" />
        </span>
      </div>
      <div class="download-cart-title">Waiting for an available download slot...</div>
    </>
  );
}

/**
 * Terminal failure. `stalled` means the archive builder never reported a single
 * file — historically that rendered nothing and left the cart spinning, so it
 * gets its own message rather than being folded into "failed".
 */
function FailedView({ stalled }: { stalled: boolean }) {
  return (
    <>
      <div class="download-cart-progress">
        <span style="font-size: 2em; position:relative; bottom: 15px; left: 2px;">
          <i class="fa fa-exclamation-triangle" />
        </span>
      </div>
      <div class="download-cart-title">
        {stalled
          ? "Your download did not start. Please try again."
          : "Your download could not be prepared. Please try again."}
      </div>
      <div class="download-cart-button">
        <span
          class="button medium"
          onClick={() => {
            // Close the overlay without touching the cart contents — the failed
            // job is no longer IN_PROGRESS, so getCart shows the items again and
            // the user can simply hit Download a second time.
            fadeOut("download-cart");
            hide("download-cart-back");
          }}
        >
          Close
        </span>
      </div>
    </>
  );
}

export interface DownloadStatusResponse {
  state?: string;
  perc?: number;
  dlId?: string;
  dlUrl?: string | undefined;
}

export function mountDownloadProgress(
  container: HTMLElement,
  response: DownloadStatusResponse
): void {
  if (response.state === "completed") {
    render(
      <CompletedView perc={response.perc ?? 0} dlId={response.dlId ?? ""} dlUrl={response.dlUrl} />,
      container
    );
  } else if (response.state === "in-progress") {
    render(<InProgressView perc={response.perc ?? 0} />, container);
  } else if (response.state === "waiting") {
    render(<WaitingView />, container);
  } else if (response.state === "failed" || response.state === "stalled") {
    render(<FailedView stalled={response.state === "stalled"} />, container);
  } else if (response.state === "none") {
    // No job row at all — nothing to report on. Clear the container instead of
    // leaving the previous frame up while the poll loop stops.
    render(null, container);
  }
}
